import { jsonFormToDefaultValues } from '@europrocurement/flexy-form';
import {
    CatalogApiObject,
    CollectionDeDossierApi,
    Contact,
    FormulaireFormaliteApi,
    Prescripteur,
    SousClientApi,
} from '@europrocurement/l2d-domain';
import {
    FproPrestationJsonldCollectiondossierCreate,
    PrestationApi,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';

export type createCollectionFormaliteProps = {
    prescripteur: Prescripteur;
    contact: Contact;
    // idFormalite: Array<number>;
    // idCatalogs: Array<number>;
    catalogs?: CatalogApiObject[];
    collectionApi: CollectionDeDossierApi;
    formulairesApi: FormulaireFormaliteApi;
    xIdSociete: number;
    libelle?: string;
    sousClient?: SousClientApi;
    sousClientOpere?: SousClientApi;
    infoComp?: string;
    societeRef?: string;
    societeDenomination?: string;
};

const catalogToPrestation = (
    catalog: CatalogApiObject,
    formulaire: string,
): FproPrestationJsonldCollectiondossierCreate => {
    if (!catalog['@id']) {
        throw new Error('missing catalog');
    }
    if (!catalog.package) {
        throw new Error('missing package');
    }
    if (!catalog.package.formalite) {
        throw new Error('missing formalite');
    }

    return {
        libelle: catalog.nomCommercial,
        deleted: false,
        catalogue: catalog['@id'].replace('/offre/catalogues', '/formalite/catalogues'),
        formalite: catalog.package.formalite.replace('/offre/formalite', '/formalite/formalites'),
        json: formulaire,
    };
};

export const getPrestationsPayload = async (
    catalogs: Array<CatalogApiObject>,
    formulairesApi: FormulaireFormaliteApi,
): Promise<Array<FproPrestationJsonldCollectiondossierCreate>> => {
    const formsBuffer = new Map();
    const formulairePromises = catalogs
        .map((catalog) => catalog?.package?.formalite?.formulaire?.id)
        .filter((formulaireId) => formulaireId !== undefined)
        .map((formulaireId) =>
            formulairesApi.apiFormulaireFormalitesIdGet({ id: formulaireId as unknown as string }),
        );

    (await Promise.all(formulairePromises)).forEach((r) => {
        const { data } = r;
        const formulaireId = data?.id as number;
        const rawForm = data.formulaire;
        formsBuffer.set(formulaireId, JSON.parse(rawForm));
    });

    return catalogs.map((catalog) => {
        const formulaireId = catalog.package?.formalite?.formulaire?.id;
        let formulaire = '{}';

        if (formulaireId && formsBuffer.has(formulaireId as number)) {
            const t = jsonFormToDefaultValues(formsBuffer.get(formulaireId as number));
            formulaire = JSON.stringify(t);
        }

        return catalogToPrestation(catalog, formulaire);
    });
};

export const createCollectionFormalite = async ({
    prescripteur,
    contact,
    catalogs,
    collectionApi,
    formulairesApi,
    xIdSociete,
    libelle,
    sousClient,
    sousClientOpere,
    infoComp,
    societeRef,
}: createCollectionFormaliteProps) => {
    if (!prescripteur.id) {
        throw new Error('missing prescriber');
    }

    if (!catalogs || catalogs.length === 0) {
        throw new Error('missing catalog');
    }
    if (!sousClient || !sousClient.id) {
        throw new Error('missing sousClient');
    }

    const prestations = await getPrestationsPayload(catalogs, formulairesApi);

    const res = await collectionApi.apiCollectionsDossiersPost({
        collectionDeDossierJsonldCollectiondossierCreate: {
            dossiers: [
                {
                    contact: contact.id,
                    societeRef,
                    societeDenomination: sousClient.raisonSociale,
                    origine: 13,
                    // "idGreffe": 106,
                    societeSiren: sousClient.siren,
                    clientOpere: sousClientOpere ? sousClientOpere.id : sousClient.id,
                    idFormeJuridique: 21,
                    formaliste: prescripteur.formaliste?.id,
                    // "acompteAttendu": "500.00",
                    // "affaire": "affaire du dossier",
                    prestations,
                },
            ],
            prescripteur: `/formalite/tiers/${prescripteur.id}`,
            client: `/formalite/tiers/${sousClient.id}`,
            societe: xIdSociete,
            libelle,
            infoComp,
        },
    });

    if (!res.data) {
        throw new Error('missing collection');
    }

    return res.data;
};

export const updatePrescriptionCollection = async (
    idCollection: number | string,
    collectionApi: CollectionDeDossierApi,
    prescripteur: number | string,
) => {
    const freshCollection = (
        await collectionApi.apiCollectionsDossiersIdGet({ id: `${idCollection}` })
    ).data;
    if (!freshCollection || !freshCollection.id) {
        throw new Error('error in Collection');
    }
    await collectionApi.apiCollectionsDossiersIdPatch({
        id: `${idCollection}`,
        collectionDeDossierCollectiondossierUpdate: {
            client: `/formalite/tiers/${prescripteur}`,
            typeFacturation: freshCollection.typeFacturation,
        },
    });
};

export const addDataToCollection = async (
    idCollection: number | string,
    collectionApi: CollectionDeDossierApi,
    data: string,
) => {
    const freshCollection = (
        await collectionApi.apiCollectionsDossiersIdGet({ id: `${idCollection}` })
    ).data;
    if (!freshCollection || !freshCollection.id) {
        throw new Error('error in Collection');
    }
    await collectionApi.apiCollectionsDossiersIdPut({
        id: `${idCollection}`,
        collectionDeDossierJsonldCollectiondossierUpdate: {
            ...freshCollection,
            // prescripteur: freshCollection.prescripteur?.replace('/tiers/tiers', '/formalite/tiers'),
            // client: freshCollection.client?.replace('/tiers/tiers', '/formalite/tiers'),
            infoComp: data,
        },
    });
};

export const updateJsonPrestationFormalite = async (
    prestationFormaliteApi: PrestationApi,
    prestaId: number | null,
    json: string,
) => {
    // const prestaId = presta.id ? presta.id : castIriToId(presta['@id']);

    if (!prestaId) {
        throw new Error('missing prestaId');
    }
    const freshPresta = (
        await prestationFormaliteApi.apiPrestationsIdGet({
            id: `${prestaId}`,
        })
    ).data;

    if (!freshPresta.catalogue) {
        throw new Error('no catalog in presta');
    }

    if (!freshPresta.catalogue['@id']) {
        throw new Error('error in presta');
    }

    await prestationFormaliteApi.apiPrestationsIdPut({
        id: `${prestaId}`,
        prestationJsonldPrestationUpdate: {
            libelle: freshPresta.libelle,
            json: JSON.stringify({
                ...JSON.parse(freshPresta.json || '{}'),
                ...JSON.parse(json),
            }),
        },
    });
};
